import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { variables } from '@splunk/themes';

import {
    useSelector,
    selectMode,
    selectTitle,
    selectDescription,
    useDispatch,
    updateTitle,
    updateDescription,
    selectLayoutGlobalOptions,
} from '@splunk/dashboard-state';
import { useHeaderRef } from '@splunk/dashboard-context';

import Title, { LINE_HEIGHT } from '../header/Title';
import Description from '../header/Description';
import { useTextInput } from '../hooks/useTextInput';

import { CollapseHandle } from './CollapseHandle';

const HeaderContainer = styled.div`
    padding: 8px 8px 0px 8px;
    background-color: ${variables.backgroundColorPage};
    box-sizing: border-box;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: flex-start;
    min-height: ${LINE_HEIGHT};
    box-sizing: inherit;
`;

export const ConnectedDashboardHeader = () => {
    const [isExpanded, setIsExpanded] = useState(true);
    const dispatch = useDispatch();
    const mode = useSelector(selectMode);
    const title = useSelector(selectTitle);
    const description = useSelector(selectDescription);
    const { showTitleAndDescription = true } = useSelector(
        selectLayoutGlobalOptions
    );
    const containerRef = useHeaderRef('container');
    const titleRef = useHeaderRef('title');
    const descRef = useHeaderRef('description');
    const handleRef = useHeaderRef('collapseHandle');

    const handleExpandToggle = useCallback(() => {
        setIsExpanded((isExpandedVal) => !isExpandedVal);
    }, []);

    const handleTitleCommit = useCallback(
        (val?: string) => dispatch(updateTitle(val)),
        [dispatch]
    );

    const {
        onHandleBlur: onHandleBlurTitle,
        onHandleChange: onHandleChangeTitle,
        onHandleKeyDown: onHandleKeyDownTitle,
        value: valueTitle,
    } = useTextInput({
        initialValue: title,
        handleCommit: handleTitleCommit,
        uniqKey: title ?? '',
    });

    const handleDescCommit = useCallback(
        (val?: string) => dispatch(updateDescription(val)),
        [dispatch]
    );

    const {
        onHandleBlur: onHandleBlurDesc,
        onHandleChange: onHandleChangeDesc,
        onHandleKeyDown: onHandleKeyDownDesc,
        value: valueDesc,
    } = useTextInput({
        initialValue: description,
        handleCommit: handleDescCommit,
        uniqKey: description ?? '',
    });

    if (
        !showTitleAndDescription ||
        (!valueTitle && !valueDesc && mode === 'view')
    ) {
        // In this scenario, do not render this component at all as we want to maximize canvas space.
        return null;
    }

    return (
        <HeaderContainer data-test="dashboard-header" ref={containerRef}>
            {(mode !== 'view' || !!valueTitle) && (
                <TitleContainer data-test="dashboard-header-title">
                    <Title
                        ref={titleRef}
                        handleBlur={onHandleBlurTitle}
                        handleChange={onHandleChangeTitle}
                        handleKeyDown={onHandleKeyDownTitle}
                        isExpanded={isExpanded}
                        mode={mode}
                        value={valueTitle}
                    />
                    <CollapseHandle
                        ref={handleRef}
                        isExpanded={isExpanded}
                        mode={mode}
                        description={valueDesc}
                        onClick={handleExpandToggle}
                    />
                </TitleContainer>
            )}
            {(mode !== 'view' || !!valueDesc) && (
                <Description
                    ref={descRef}
                    handleBlur={onHandleBlurDesc}
                    handleChange={onHandleChangeDesc}
                    handleKeyDown={onHandleKeyDownDesc}
                    showInViewMode={isExpanded}
                    mode={mode}
                    value={valueDesc}
                />
            )}
        </HeaderContainer>
    );
};
